import dayjs from 'dayjs';
import jalaliPlugin from 'dayjs-jalali';
import 'dayjs/locale/fa';

dayjs.extend(jalaliPlugin);

// ── Pure-JS Jalali ↔ Gregorian
// Algorithm taken verbatim from jalaali-js (node_modules/jalaali-js/index.js).
// dayjs-jalali@0.0.2 uses n.padStart which crashes in dayjs@1.11.x, so we
// avoid { jalali: true } parsing and 'MM'/'DD' format tokens entirely.

const _div = (a, b) => ~~(a / b);
const _mod = (a, b) => a - ~~(a / b) * b;

const _breaks = [-61,9,38,199,426,686,756,818,1111,1181,1210,1635,2060,2097,2192,2262,2324,2394,2456,3178];

function _jalCal(jy) {
  const bl = _breaks.length;
  let gy = jy + 621, leapJ = -14, jp = _breaks[0];
  let jm, jump, leap, leapG, march, n;

  if (jy < jp || jy >= _breaks[bl - 1]) throw new Error('Invalid Jalali year ' + jy);

  for (let i = 1; i < bl; i++) {
    jm = _breaks[i];
    jump = jm - jp;
    if (jy < jm) break;
    leapJ = leapJ + _div(jump, 33) * 8 + _div(_mod(jump, 33), 4);
    jp = jm;
  }
  n = jy - jp;

  leapJ = leapJ + _div(n, 33) * 8 + _div(_mod(n, 33) + 3, 4);
  if (_mod(jump, 33) === 4 && jump - n === 4) leapJ++;

  leapG = _div(gy, 4) - _div((_div(gy, 100) + 1) * 3, 4) - 150;
  march = 20 + leapJ - leapG;

  if (jump - n < 6) n = n - jump + _div(jump + 4, 33) * 33;
  leap = _mod(_mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;

  return { leap, gy, march };
}

function _g2d(gy, gm, gd) {
  let d = _div((gy + _div(gm - 8, 6) + 100100) * 1461, 4) +
          _div(153 * _mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  d = d - _div(_div(gy + 100100 + _div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}

function _d2g(jdn) {
  let j = 4 * jdn + 139361631 + _div(_div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  let i = _div(_mod(j, 1461), 4) * 5 + 308;
  const gd = _div(_mod(i, 153), 5) + 1;
  const gm = _mod(_div(i, 153), 12) + 1;
  const gy = _div(j, 1461) - 100100 + _div(8 - gm, 6);
  return { gy, gm, gd };
}

function _j2d(jy, jm, jd) {
  const r = _jalCal(jy);
  return _g2d(r.gy, 3, r.march) + (jm - 1) * 31 - _div(jm, 7) * (jm - 7) + jd - 1;
}

export function jalaliToGregorian(jy, jm, jd) {
  return _d2g(_j2d(jy, jm, jd));
}

export function isJalaliLeap(jy) {
  return _jalCal(jy).leap === 0;
}

// ── Safe dayjs-jalali wrappers ───────────────────────────────────────────────
// Only 'YYYY', 'M', 'D' tokens work with locale('fa') in dayjs-jalali@0.0.2.
// 'MM' / 'DD' crash (plugin uses n.padStart which doesn't exist in dayjs@1.11.x).

function _jParts(date) {
  const d = dayjs(date).locale('fa');
  if (!d.isValid()) return null;
  const y   = parseInt(d.format('YYYY'));
  const m   = parseInt(d.format('M'));
  const day = parseInt(d.format('D'));
  return { y, m, day };
}

export function toJalali(date) {
  if (!date) return '—';
  const p = _jParts(date);
  if (!p) return '—';
  return `${p.y}/${String(p.m).padStart(2, '0')}/${String(p.day).padStart(2, '0')}`;
}

export function toGregorian(date, format = 'YYYY/MM/DD') {
  if (!date) return '—';
  const d = dayjs(date);
  if (!d.isValid()) return '—';
  return d.format(format);
}

export function jalaliToIso(jalaliStr) {
  if (!jalaliStr) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(jalaliStr)) return jalaliStr;
  const parts = jalaliStr.split(/[/\-]/);
  if (parts.length !== 3) return jalaliStr;
  const [jy, jm, jd] = parts.map(Number);
  if (!jy || !jm || !jd) return jalaliStr;
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`;
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

export const JALALI_MONTHS = [
  'فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور',
  'مهر','آبان','آذر','دی','بهمن','اسفند',
];
export const JALALI_DAYS_SHORT    = ['ش','ی','د','س','چ','پ','ج']; // Sat→Fri
export const GREGORIAN_MONTHS     = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
export const GREGORIAN_DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export function todayJalali() {
  const d = dayjs().locale('fa');
  return { year: parseInt(d.format('YYYY')), month: parseInt(d.format('M')) };
}

export function jalaliDayLabel(iso) {
  if (!iso) return '';
  const p = _jParts(iso);
  if (!p) return iso;
  return `${p.day} ${JALALI_MONTHS[p.m - 1]} ${p.y}`;
}

function _todayIso() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function _isoFromGreg(gy, gm, gd) {
  return `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`;
}

/**
 * Jalali calendar grid.  Week starts Saturday (Iranian convention).
 * Cell: { mainDay, secondaryDay (Gregorian day), iso, isToday, dow (0=Sat…6=Fri) }
 */
export function buildJalaliGrid(jYear, jMonth) {
  const { gy: gy1, gm: gm1, gd: gd1 } = jalaliToGregorian(jYear, jMonth, 1);
  const firstJsDow = new Date(gy1, gm1 - 1, gd1).getDay(); // 0=Sun..6=Sat
  const startCol   = (firstJsDow + 1) % 7; // Iran: Sat=0, Sun=1 … Fri=6

  const daysInMonth = jMonth <= 6 ? 31 : jMonth <= 11 ? 30 : isJalaliLeap(jYear) ? 30 : 29;
  const todayIso = _todayIso();

  const cells = Array(startCol).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const { gy, gm, gd } = jalaliToGregorian(jYear, jMonth, d);
    const iso = _isoFromGreg(gy, gm, gd);
    const dow = (new Date(gy, gm - 1, gd).getDay() + 1) % 7; // 0=Sat..6=Fri
    cells.push({ mainDay: d, secondaryDay: gd, iso, isToday: iso === todayIso, dow });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * Gregorian calendar grid.  Week starts Monday.
 * Cell: { mainDay, secondaryDay (Jalali day), iso, isToday, dow (0=Mon…6=Sun) }
 */
export function buildGregorianGrid(year, month) {
  const firstJsDow = new Date(year, month - 1, 1).getDay(); // 0=Sun..6=Sat
  const startCol   = (firstJsDow + 6) % 7; // Mon=0..Sun=6

  const daysInMonth = new Date(year, month, 0).getDate();
  const todayIso    = _todayIso();

  const cells = Array(startCol).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = _isoFromGreg(year, month, d);
    const p   = _jParts(iso);
    const dow = (new Date(year, month - 1, d).getDay() + 6) % 7; // 0=Mon..6=Sun
    cells.push({ mainDay: d, secondaryDay: p ? p.day : 0, iso, isToday: iso === todayIso, dow });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
